import { useCallback, useLayoutEffect, useRef, useState } from 'react';

const DEFAULT_SUFFIX = '… Show More';

/**
 * Measures whether a container exceeds a max height when rendering the given text,
 * and if so, binary-searches for the longest truncation that fits.
 *
 * The container must include an element with `data-content-text` where the text is rendered.
 */
export default function useTruncatedContent(
  content: string | undefined,
  maxHeight: number,
  enabled: boolean,
  suffix: string = DEFAULT_SUFFIX
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [needsShowMore, setNeedsShowMore] = useState(false);
  const [truncatedContent, setTruncatedContent] = useState('');

  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container || !content || !enabled) {
      setNeedsShowMore(false);
      return;
    }

    // Clone the container off-screen for measurement
    const clone = container.cloneNode(true) as HTMLElement;
    clone.style.position = 'absolute';
    clone.style.visibility = 'hidden';
    clone.style.height = 'auto';
    clone.style.width = `${container.offsetWidth}px`;
    clone.style.left = '-9999px';
    document.body.appendChild(clone);

    const contentEl = clone.querySelector<HTMLElement>('[data-content-text]');

    if (!contentEl) {
      // eslint-disable-next-line no-console
      console.warn('useTruncatedContent: no element with [data-content-text] found in container');
      document.body.removeChild(clone);
      return;
    }

    // Render full text and measure
    contentEl.textContent = content;
    if (clone.scrollHeight <= maxHeight) {
      setNeedsShowMore(false);
      document.body.removeChild(clone);
      return;
    }

    // Height available for text = threshold - (container height without text)
    contentEl.textContent = '';
    const nonTextHeight = clone.scrollHeight;
    const availableTextHeight = maxHeight - nonTextHeight;

    // Binary search for the longest substring + suffix that fits
    let low = 0;
    let high = content.length;

    while (low < high) {
      const mid = Math.ceil((low + high) / 2);
      contentEl.textContent = content.slice(0, mid) + suffix;
      if (contentEl.scrollHeight <= availableTextHeight) {
        low = mid;
      } else {
        high = mid - 1;
      }
    }

    document.body.removeChild(clone);
    setTruncatedContent(content.slice(0, low));
    setNeedsShowMore(true);
  }, [content, maxHeight, enabled, suffix]);

  useLayoutEffect(() => {
    measure();

    let timeoutId: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(measure, 150);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [measure]);

  return { containerRef, needsShowMore, truncatedContent };
}
